import { FC, useEffect, useState } from 'react';
import {
  TYPE_FACEBOOK,
  TYPE_LINKEDIN,
} from '@kontentino/kontentino-constants/Pages';
import { mapApiDataToFormValues } from './../utils/mapApiDataToFormValues';
import { FormTab } from 'app/modules/posts/modules/detail/modules/audienceRestrictions/constants/audienceRestrictions';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import {
  AudienceRestrictions,
  AudienceRestrictionsOptions,
} from 'app/modules/posts/types/AudienceRestrictionsOptions';
import useSetState from 'utils/hooks/useSetState';
import ActiveFormTab from 'app/modules/posts/modules/detail/modules/audienceRestrictions/components/form/ActiveFormTab';
import AudienceRestrictionsApi from 'app/modules/posts/api/audienceRestrictions';
import PageApi from 'api/page';
import { useQuery, useMutation } from 'react-query';
import {
  convertOptionsToValues,
  getFormFields,
  createCompleteFormState,
} from 'app/modules/posts/modules/detail/modules/audienceRestrictions/utils/audienceRestrictions';
import { queryKey } from 'constants/queryKey';
import { Alert, Button, Tabs } from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import { useToast } from 'app/hooks/useToast';
import PostsConfig from 'app/config/posts';
import { ApiClientError } from 'api/client';

type Props = {
  page: {
    id?: number;
    type: number;
  };
  onSubmit: (value: AudienceRestrictions) => void;
  onClose: () => void;
  initialValue?: AudienceRestrictions | null;
  // Only a live post must meet LinkedIn's minimum audience; a preset is a
  // template and always saves the selected restrictions.
  enforceAudienceSizeLimit?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
};

const Form: FC<Props> = ({
  page,
  initialValue,
  onClose,
  onSubmit,
  enforceAudienceSizeLimit = true,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<FormTab>(
    FormTab.RestrictedAudience,
  );
  const [formState, setFormState] = useSetState<AudienceRestrictionsOptions>(
    {},
  );
  const toast = useToast();
  const [showAudienceSizeWarning, setShowAudienceSizeWarning] = useState(false);
  const [baseline, setBaseline] = useState<string | null>(null);

  const {
    data: audienceRestrictionsData,
    isLoading: isLoadingAudienceOptions,
    isError: isErrorAudienceOptions,
    isRefetching: isRefetchingAudienceOptions,
  } = useQuery(
    [
      page.id === undefined
        ? queryKey.audienceRestrictionsForNetwork(page.type)
        : queryKey.pageDetailWithAudienceRestrictions(page.id),
      initialValue,
    ],
    async () => {
      const pageDetail =
        page.id === undefined ? null : await PageApi.detail(page.id);
      const dataToLoad = initialValue ?? pageDetail?.audienceRestrictions ?? {};
      const audienceOptions =
        await AudienceRestrictionsApi.getOptionsByOptionIds(
          dataToLoad,
          page.type,
        );

      return { pageDetail, audienceOptions };
    },
    {
      onSuccess: (data) => {
        const mappedValues = mapApiDataToFormValues(data.audienceOptions);
        const completeFormState = createCompleteFormState(mappedValues);

        setFormState(() => completeFormState);
        setBaseline(JSON.stringify(convertOptionsToValues(completeFormState)));
      },
    },
  );

  useEffect(() => {
    if (!onDirtyChange || baseline === null) return;
    onDirtyChange(
      JSON.stringify(convertOptionsToValues(formState)) !== baseline,
    );
  }, [formState, baseline, onDirtyChange]);
  const pageId = page.id;
  const { data: audienceSize, isLoading: isLoadingAudienceSize } = useQuery(
    queryKey.audienceSize({ pageId: pageId ?? 0, formState }),
    async () => {
      if (pageId === undefined) return undefined;

      return AudienceRestrictionsApi.getAudienceSize(
        (convertOptionsToValues(formState) || initialValue) ??
          audienceRestrictionsData?.pageDetail?.audienceRestrictions ??
          {},
        pageId,
      );
    },
    {
      enabled:
        pageId !== undefined &&
        page.type === TYPE_LINKEDIN &&
        !!audienceRestrictionsData?.pageDetail?.audienceRestrictions,
    },
  );
  const loadPresetMutation = useMutation(
    (audienceRestrictions: AudienceRestrictions) =>
      AudienceRestrictionsApi.getOptionsByOptionIds(
        audienceRestrictions,
        page.type,
      ),
    {
      onSuccess: (data) => {
        if (data) {
          const mappedValues = mapApiDataToFormValues(data);
          const completeFormState = createCompleteFormState(mappedValues);

          setFormState(() => completeFormState);
        }
      },
      onError: (e: ApiClientError) => {
        toast(
          e?.userMessage || t('audienceRestrictions.failedToLoadPreset'),
          'error',
        );
      },
    },
  );

  const handlePresetSelect = async (
    audienceRestrictions: AudienceRestrictions,
  ) => {
    setActiveTab(FormTab.RestrictedAudience);
    loadPresetMutation.mutate(audienceRestrictions);
  };

  const shouldShowAudienceSizeWarning = (audienceSize: any) => {
    const hasValidAudienceSize = audienceSize?.size !== undefined;
    const isBelowMinimumSize =
      hasValidAudienceSize &&
      audienceSize.size <
        PostsConfig.AUDIENCE_RESTRICTIONS.MINIMUM_LI_AUDIENCE_SIZE;
    const cannotCalculateAudience = !hasValidAudienceSize;

    return isBelowMinimumSize || cannotCalculateAudience;
  };

  const handleSave = () => {
    const formValues = convertOptionsToValues(formState);

    if (
      enforceAudienceSizeLimit &&
      page.type === TYPE_LINKEDIN &&
      isLoadingAudienceSize
    ) {
      return;
    }

    if (
      enforceAudienceSizeLimit &&
      page.type === TYPE_LINKEDIN &&
      shouldShowAudienceSizeWarning(audienceSize)
    ) {
      setShowAudienceSizeWarning(true);
      return;
    }

    onSubmit(formValues);
  };

  const handleConfirmSaveWithSmallAudience = () => {
    setShowAudienceSizeWarning(false);
    setFormState({});
    onSubmit({});
  };

  return (
    <>
      <Modal.Content>
        <Tabs.Root value={activeTab.toString()}>
          <Tabs.List>
            <Tabs.Tab
              onClick={() => setActiveTab(FormTab.RestrictedAudience)}
              value={FormTab.RestrictedAudience.toString()}
            >
              {t('audienceRestrictions.restrictedAudience')}
            </Tabs.Tab>
            {page.type === TYPE_FACEBOOK && (
              <Tabs.Tab
                onClick={() => setActiveTab(FormTab.NewsFeedTargeting)}
                value={FormTab.NewsFeedTargeting.toString()}
              >
                {t('audienceRestrictions.newsFeedTargeting')}
              </Tabs.Tab>
            )}
            <Tabs.Tab
              onClick={() => setActiveTab(FormTab.Presets)}
              value={FormTab.Presets.toString()}
            >
              {t('audienceRestrictions.presets')}
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.Root>
        {audienceRestrictionsData &&
          !isRefetchingAudienceOptions &&
          !loadPresetMutation.isLoading && (
            <ActiveFormTab
              key={JSON.stringify(formState)}
              page={page}
              onChange={setFormState}
              initialValue={formState}
              activeTab={activeTab}
              linkedinAudience={
                page.id === undefined
                  ? undefined
                  : {
                      size: audienceSize?.size,
                      loading: isLoadingAudienceSize,
                    }
              }
              onPresetSelect={handlePresetSelect}
            />
          )}
        {(isLoadingAudienceOptions ||
          isRefetchingAudienceOptions ||
          loadPresetMutation.isLoading) && (
          <div className="tw-flex tw-flex-col tw-gap-3">
            {getFormFields(page.type, activeTab).map((field) => (
              <Skeleton height={40} key={field.type} />
            ))}
          </div>
        )}
        {isErrorAudienceOptions && (
          <Alert variant="danger">{t('somethingWentWrongTryAgain')}</Alert>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          isLoading={
            enforceAudienceSizeLimit &&
            page.type === TYPE_LINKEDIN &&
            isLoadingAudienceSize
          }
        >
          {t('save')}
        </Button>
      </Modal.Footer>

      {showAudienceSizeWarning && (
        <Modal>
          <Modal.Content>
            <div className="tw-p-4 tw-pt-8">
              <Alert variant="warning">
                {audienceSize?.size !== undefined
                  ? t('audienceRestrictions.audienceTooSmallWithSize', {
                      size: audienceSize.size,
                      minSize:
                        PostsConfig.AUDIENCE_RESTRICTIONS
                          .MINIMUM_LI_AUDIENCE_SIZE,
                    })
                  : t('audienceRestrictions.audienceTooSmallUnknownSize', {
                      minSize:
                        PostsConfig.AUDIENCE_RESTRICTIONS
                          .MINIMUM_LI_AUDIENCE_SIZE,
                    })}
              </Alert>
            </div>
          </Modal.Content>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAudienceSizeWarning(false)}
            >
              {t('cancel')}
            </Button>
            <Button onClick={handleConfirmSaveWithSmallAudience}>
              {t('audienceRestrictions.continueWithoutRestrictions')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default Form;
